/**
 * Patterns Viewer
 * Interactive display of complex patterns (Page templates, Workflows)
 */

import React, { useState } from "react";
import {
  Box,
  Text,
  Heading,
  Button,
  Divider,
  Card,
  CardBody,
  Badge,
  SimpleGrid,
  Avatar,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Progress,
  Step,
  StepDescription,
  StepIcon,
  StepIndicator,
  StepNumber,
  StepSeparator,
  StepStatus,
  StepTitle,
  Stepper,
  useSteps,
} from "@chakra-ui/react";
import CDS, { useResolveColor } from "design-system";
import { ChevronRightIcon } from "@chakra-ui/icons";

function PageTemplateExamples() {
  const exampleBg = useResolveColor(CDS.Color.surface.secondary);

  return (
    <CDS.Stack align={CDS.Stack.align.stretch} spacing={CDS.Spacing.large}>
      <Heading size="sm">Page Templates</Heading>

      <SimpleGrid columns={1} spacing={6}>
        <Card>
          <CardBody>
            <CDS.Stack align={CDS.Stack.align.stretch} spacing={CDS.Spacing.medium}>
              <Text fontWeight="semibold">Standard Page Layout</Text>

              <Box bg={exampleBg} p={4} borderRadius="md" minH="400px">
                <CDS.Stack align={CDS.Stack.align.stretch} spacing={CDS.Spacing.medium} h="full">
                  {/* Page Header */}
                  <Box bg="white" p={4} borderRadius="md" border="2px dashed" borderColor="blue.200">
                    <CDS.Stack align={CDS.Stack.align.start} spacing={CDS.Spacing.small}>
                      <Breadcrumb spacing={CDS.Spacing.small} separator={<ChevronRightIcon color="gray.500" />}>
                        <BreadcrumbItem>
                          <BreadcrumbLink fontSize="sm">Admin</BreadcrumbLink>
                        </BreadcrumbItem>
                        <BreadcrumbItem isCurrentPage>
                          <BreadcrumbLink fontSize="sm">Users</BreadcrumbLink>
                        </BreadcrumbItem>
                      </Breadcrumb>

                      <CDS.Stack direction={CDS.Stack.direction.horizontal} justify={CDS.Stack.justify.between} w="full">
                        <CDS.Stack align={CDS.Stack.align.start} spacing={CDS.Spacing.tiny}>
                          <Heading size="lg">User Management</Heading>
                          <Text color="gray.600">Manage user accounts and permissions</Text>
                        </CDS.Stack>
                        <CDS.Stack direction={CDS.Stack.direction.horizontal}>
                          <Button variant="outline" size="sm">Export</Button>
                          <Button size="sm">Add User</Button>
                        </CDS.Stack>
                      </CDS.Stack>
                    </CDS.Stack>
                  </Box>

                  {/* Page Content */}
                  <Box bg="white" p={4} borderRadius="md" border="2px dashed" borderColor="green.200" flex={1}>
                    <Text textAlign="center" color="gray.600" fontSize="sm">
                      Main Content Area
                      <br />
                      Tables, forms, cards, and other content goes here
                    </Text>
                  </Box>
                </CDS.Stack>
              </Box>

              <Text fontSize="sm" color="gray.600" fontFamily="mono">
                {'<CDS.Page title="User Management" subtitle="Manage accounts" actions={actions}>'}
              </Text>
            </CDS.Stack>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <CDS.Stack align={CDS.Stack.align.stretch} spacing={CDS.Spacing.medium}>
              <Text fontWeight="semibold">Detail Page Layout</Text>

              <Box bg={exampleBg} p={4} borderRadius="md" minH="400px">
                <CDS.Stack direction={CDS.Stack.direction.horizontal} align={CDS.Stack.align.start} spacing={CDS.Spacing.medium} h="full">
                  {/* Sidebar */}
                  <Box bg="white" p={4} borderRadius="md" border="2px dashed" borderColor="purple.200" w="250px">
                    <CDS.Stack align={CDS.Stack.align.stretch} spacing={CDS.Spacing.small}>
                      <CDS.Stack direction={CDS.Stack.direction.horizontal}>
                        <Avatar size="sm" name="John Doe" />
                        <CDS.Stack align={CDS.Stack.align.start} spacing={CDS.Spacing.tiny}>
                          <Text fontWeight="semibold" fontSize="sm">John Doe</Text>
                          <Text fontSize="xs" color="gray.600">john@example.com</Text>
                        </CDS.Stack>
                      </CDS.Stack>

                      <Divider />

                      <CDS.Stack align={CDS.Stack.align.stretch} spacing={CDS.Spacing.small} fontSize="sm">
                        <Button variant="ghost" size="sm" justifyContent="start">Profile</Button>
                        <Button variant="ghost" size="sm" justifyContent="start">Activity</Button>
                        <Button variant="ghost" size="sm" justifyContent="start">Settings</Button>
                      </CDS.Stack>
                    </CDS.Stack>
                  </Box>

                  {/* Main Content */}
                  <Box bg="white" p={4} borderRadius="md" border="2px dashed" borderColor="orange.200" flex={1}>
                    <Text textAlign="center" color="gray.600" fontSize="sm">
                      Detail Content Area
                      <br />
                      Forms, tabs, detailed information
                    </Text>
                  </Box>
                </CDS.Stack>
              </Box>

              <Text fontSize="sm" color="gray.600" fontFamily="mono">
                {'<CDS.Page layout="detail" sidebar={sidebar}>'}
              </Text>
            </CDS.Stack>
          </CardBody>
        </Card>
      </SimpleGrid>
    </CDS.Stack>
  );
}

function WorkflowExamples() {
  const exampleBg = useResolveColor(CDS.Color.surface.secondary);

  const steps = [
    { title: "Basic Info", description: "Name and contact details" },
    { title: "Company Details", description: "Organization information" },
    { title: "Preferences", description: "Settings and options" },
    { title: "Review", description: "Confirm and submit" },
  ];

  const { activeStep, setActiveStep } = useSteps({
    index: 1,
    count: steps.length,
  });

  return (
    <CDS.Stack align={CDS.Stack.align.stretch} spacing={CDS.Spacing.large}>
      <Heading size="sm">Workflow Patterns</Heading>

      <SimpleGrid columns={1} spacing={6}>
        <Card>
          <CardBody>
            <CDS.Stack align={CDS.Stack.align.stretch} spacing={CDS.Spacing.large}>
              <Text fontWeight="semibold">Multi-Step Workflow</Text>

              <Stepper index={activeStep} colorScheme="blue">
                {steps.map((step, index) => (
                  <Step key={index} onClick={() => setActiveStep(index)}>
                    <StepIndicator>
                      <StepStatus
                        complete={<StepIcon />}
                        incomplete={<StepNumber />}
                        active={<StepNumber />}
                      />
                    </StepIndicator>

                    <Box flexShrink="0">
                      <StepTitle>{step.title}</StepTitle>
                      <StepDescription>{step.description}</StepDescription>
                    </Box>

                    <StepSeparator />
                  </Step>
                ))}
              </Stepper>

              <Box bg={exampleBg} p={6} borderRadius="md" minH="200px">
                <CDS.Stack spacing={CDS.Spacing.medium}>
                  <Heading size="md">{steps[activeStep]?.title}</Heading>
                  <Text color="gray.600" textAlign="center">
                    {steps[activeStep]?.description}
                  </Text>

                  <Box w="full" maxW="md">
                    <Text fontSize="sm" color="gray.600" mb={2}>Progress</Text>
                    <Progress
                      value={((activeStep + 1) / steps.length) * 100}
                      colorScheme="blue"
                      borderRadius="full"
                    />
                  </Box>

                  <CDS.Stack direction={CDS.Stack.direction.horizontal} spacing={CDS.Spacing.small} mt={6}>
                    <Button
                      variant="outline"
                      isDisabled={activeStep === 0}
                      onClick={() => setActiveStep(Math.max(0, activeStep - 1))}
                    >
                      Previous
                    </Button>
                    <Button
                      onClick={() => setActiveStep(Math.min(steps.length - 1, activeStep + 1))}
                      isDisabled={activeStep === steps.length - 1}
                    >
                      {activeStep === steps.length - 1 ? "Complete" : "Next"}
                    </Button>
                  </CDS.Stack>
                </CDS.Stack>
              </Box>

              <Text fontSize="sm" color="gray.600" fontFamily="mono">
                {'<CDS.Workflow steps={steps} currentStep="basic-info" showProgress={true}>'}
              </Text>
            </CDS.Stack>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <CDS.Stack align={CDS.Stack.align.stretch} spacing={CDS.Spacing.medium}>
              <Text fontWeight="semibold">Form Workflow Pattern</Text>

              <Box bg={exampleBg} p={4} borderRadius="md">
                <CDS.Stack align={CDS.Stack.align.stretch} spacing={CDS.Spacing.medium}>
                  <CDS.Stack direction={CDS.Stack.direction.horizontal} justify={CDS.Stack.justify.between}>
                    <Badge colorScheme="blue">Step 2 of 4</Badge>
                    <Text fontSize="sm" color="gray.600">Company Details</Text>
                  </CDS.Stack>

                  <Box bg="white" p={4} borderRadius="md" border="1px solid" borderColor="gray.200">
                    <CDS.Stack align={CDS.Stack.align.stretch} spacing={CDS.Spacing.small}>
                      <Text fontSize="sm" color="gray.600">Form fields would go here...</Text>
                      <Box h="60px" bg="gray.100" borderRadius="md" />
                      <Box h="60px" bg="gray.100" borderRadius="md" />
                      <Box h="60px" bg="gray.100" borderRadius="md" />
                    </CDS.Stack>
                  </Box>

                  <CDS.Stack direction={CDS.Stack.direction.horizontal} justify={CDS.Stack.justify.between}>
                    <Button variant="ghost" size="sm">
                      ← Back
                    </Button>
                    <CDS.Stack direction={CDS.Stack.direction.horizontal} spacing={CDS.Spacing.small}>
                      <Button variant="outline" size="sm">
                        Save Draft
                      </Button>
                      <Button size="sm">
                        Continue →
                      </Button>
                    </CDS.Stack>
                  </CDS.Stack>
                </CDS.Stack>
              </Box>

              <Text fontSize="sm" color="gray.600" fontFamily="mono">
                {"<CDS.Workflow.Step canSkip={false} autoSave={true}>"}
              </Text>
            </CDS.Stack>
          </CardBody>
        </Card>
      </SimpleGrid>
    </CDS.Stack>
  );
}

export function PatternsViewer() {
  const [activeTab, setActiveTab] = useState("page");

  const tabs = [
    { id: "page", name: "Page Templates", component: PageTemplateExamples },
    { id: "workflow", name: "Workflows", component: WorkflowExamples },
  ];

  const ActiveComponent = tabs.find((tab) => tab.id === activeTab)?.component;

  return (
    <CDS.Stack align={CDS.Stack.align.stretch} spacing={CDS.Spacing.large}>
      <CDS.Stack direction={CDS.Stack.direction.horizontal} spacing={CDS.Spacing.small}>
        {tabs.map((tab) => (
          <Button
            key={tab.id}
            size="sm"
            variant={activeTab === tab.id ? "solid" : "ghost"}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.name}
          </Button>
        ))}
      </CDS.Stack>

      <Divider />

      <CDS.Box>
        {ActiveComponent && <ActiveComponent />}
      </CDS.Box>
    </CDS.Stack>
  );
}
